import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../services/contexts/AuthContext';
import { 
  Home,
  UserCheck, 
  Heart, 
  FileText, 
  Activity, 
  TrendingUp,
  Stethoscope,
  Users,
  BarChart3,
  ClipboardList,
  Brain,
  AlertTriangle,
  CheckCircle,
  Clock,
  Zap,
  Award
} from 'lucide-react';

const TenantDoctorSidebar = ({ activeView, setActiveView }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Clinical Dashboard',
      icon: Home,
      color: 'text-[#008080]',
      path: '/tenant-doctor/dashboard'
    },
    {
      id: 'patients',
      label: 'Patient Management',
      icon: UserCheck,
      color: 'text-[#008B8B]',
      path: '/tenant-doctor/patients'
    },
    // {
    //   id: 'vitals',
    //   label: 'Vitals Analysis',
    //   icon: Heart,
    //   color: 'text-[#00BCD4]',
    //   path: '/tenant-doctor/vitals'
    // },
    // {
    //   id: 'clinical-notes',
    //   label: 'Clinical Notes',
    //   icon: FileText,
    //   color: 'text-[#5B7DAE]',
    //   path: '/tenant-doctor/clinical-notes'
    // },
    // {
    //   id: 'diagnostics',
    //   label: 'Diagnostic Review',
    //   icon: Brain,
    //   color: 'text-[#008080]',
    //   path: '/tenant-doctor/diagnostics'
    // },
    {
      id: 'analytics',
      label: 'Clinical Analytics',
      icon: BarChart3,
      color: 'text-[#00BCD4]',
      path: '/tenant-doctor/analytics'
    }
  ];

  const handleNavigation = (item) => {
    setActiveView(item.id);
    navigate(item.path);
  };

  const getCurrentPath = () => {
    return location.pathname.split('/').pop();
  };

  return (
    <div className="w-80 bg-white shadow-lg h-screen overflow-y-auto flex flex-col border-r border-[#E6EEF4]">
      {/* Logo and Brand */}
      <div className="p-6 border-b border-[#E6EEF4]">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg border border-[#E6EEF4]">
              <img 
                src="/Vital-logo.png" 
                alt="Vital Audio Logo" 
                className="h-8 w-8 object-contain"
              />
            </div>
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-[#008080] rounded-full border-2 border-white">
              <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
            </div>
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#0B1C2C]">Sonometrik</h1>
            {/* <p className="text-sm text-[#3D3D3D] font-medium">Clinical Review</p> */}
          </div>
        </div>
      </div>

      {/* User Profile Card */}
      <div className="p-4">
        <div className="bg-[#F9FBFC] p-4 rounded-xl border border-[#E6EEF4]">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-[#008080] rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-lg">
                {user?.name?.split(' ').map(n => n[0]).join('') || 'MD'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#0B1C2C] truncate">
                {user?.name || 'Dr. Physician'}
              </p>
              <p className="text-xs text-[#3D3D3D]">Clinical Specialist</p>
              <div className="flex items-center mt-1">
                <div className="w-2 h-2 bg-[#008B8B] rounded-full mr-2"></div>
                <span className="text-xs text-[#008B8B] font-medium">Clinical Access</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 px-4 space-y-1">
        <div className="mb-4">
          <p className="text-xs font-semibold text-[#5B7DAE] uppercase tracking-wide mb-3">
            CLINICAL WORKFLOW
          </p>
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = getCurrentPath() === item.id || (getCurrentPath() === '' && item.id === 'dashboard');
            
            return (
              <button
                key={item.id}
                onClick={() => handleNavigation(item)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-left transition-all duration-200 ${
                  isActive 
                    ? 'bg-[#E6EEF4] text-[#008B8B] border border-[#008B8B] shadow-sm' 
                    : 'text-[#3D3D3D] hover:text-[#0B1C2C] hover:bg-[#F9FBFC]'
                }`}
              >
                <Icon className={`h-5 w-5 ${isActive ? 'text-[#008B8B]' : item.color} transition-colors`} />
                <span className="font-medium">{item.label}</span>
                {isActive && (
                  <div className="ml-auto w-2 h-2 bg-[#008B8B] rounded-full"></div>
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Clinical Alerts */}
      {/* <div className="p-4">
        <div className="bg-[#F9FBFC] p-4 rounded-xl border border-[#E6EEF4]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[#0B1C2C]">Critical Alerts</h3>
            <AlertTriangle className="h-4 w-4 text-[#00BCD4]" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-[#3D3D3D]">Irregular Rhythms</span>
              <div className="flex items-center space-x-1">
                <AlertTriangle className="h-3 w-3 text-[#00BCD4]" />
                <span className="text-sm font-bold text-[#00BCD4]">3</span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-[#3D3D3D]">Pending Reviews</span>
              <div className="flex items-center space-x-1">
                <Clock className="h-3 w-3 text-[#5B7DAE]" />
                <span className="text-sm font-bold text-[#5B7DAE]">12</span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-[#3D3D3D]">Approved Today</span>
              <div className="flex items-center space-x-1">
                <CheckCircle className="h-3 w-3 text-[#008B8B]" />
                <span className="text-sm font-bold text-[#008B8B]">28</span>
              </div>
            </div>
          </div>
        </div>
      </div> */}

      {/* Clinical Performance */}
      {/* <div className="p-4">
        <div className="bg-[#F9FBFC] p-4 rounded-xl border border-[#E6EEF4]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[#0B1C2C]">Clinical Metrics</h3>
            <Award className="h-4 w-4 text-[#008080]" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-[#3D3D3D]">Reviews Completed</span>
              <span className="font-bold text-[#008080]">156</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#3D3D3D]">Avg Review Time</span>
              <span className="font-bold text-[#008B8B]">4.2 min</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#3D3D3D]">Accuracy Rate</span>
              <span className="font-bold text-[#00BCD4]">98.7%</span>
            </div>
          </div>
        </div>
      </div> */}

      {/* Footer */}
      <div className="p-4 border-t border-[#E6EEF4]">
        <div className="text-center">
          <p className="text-xs text-[#5B7DAE]">
            Sonometrik v1.0.0
          </p>
          <p className="text-xs text-[#5B7DAE]">
            © 2025 Healthcare Platform
          </p>
        </div>
      </div>
    </div>
  );
};

export default TenantDoctorSidebar;